import React, { useContext, useEffect, useState } from 'react';
import { Box, Grid, Text, Flex, Icon, Circle } from '@chakra-ui/react';
import { AppContext } from 'AppProvider/AppContext';
import { FaLayerGroup, FaCheckCircle } from 'react-icons/fa';
import { appRequest } from 'routes';

const ProcessLevelCards = () => {
    const {
        workflowProcess,
        accountDetails,
        activeWorkflow,
        startDate,
        endDate,
        userWorkflows,
        selectedLevel,
        setSelectedLevel,
        updateDashboardApi
    } = useContext(AppContext);

    const [levelCounts, setLevelCounts] = useState<Record<string, { count: number, loading: boolean }>>({});


    const processName = workflowProcess?.processName?.toLowerCase() || '';

    const processMaxLevels: Record<string, number> = {
        foreign: 7,
        domestic: 6,
        marcan: 5,
        tillomed: 6,
        zuventus: 6
    };

    const maxLevel = processMaxLevels[processName] || 0;
    const isAllowedProcess = maxLevel > 0;

    const levelLabels: Record<string, Record<string, string>> = {
        foreign: {
            'level-5': 'Approver-1',
            'level-6': 'Approver-2',
            'level-7': 'Approver-3',
        },
        domestic: {
            'level-4': 'Plant Code',
            'level-5': 'Manufacturing Head',
            'level-6': 'Management Head',
        }
    };

    const lastThreeLevels = isAllowedProcess ? [
        `level-${maxLevel - 2}`,
        `level-${maxLevel - 1}`,
        `level-${maxLevel}`
    ] : [];

    const fetchLevelData = async (level: string) => {
        setLevelCounts(prev => ({ ...prev, [level]: { count: 0, loading: true } }));
        try {
            const response: any = await appRequest("document", "getPendingCountByLevelRef", {
                activeWorkflow,
                level: level,
                company: accountDetails?.company?._id || accountDetails?.company,
                dateFrom: startDate,
                dateTo: endDate,
                processName: workflowProcess?.processName,
                processType: workflowProcess?.processType
            });

            if (response && response.status === "success" && response.data?.count !== undefined) {
                setLevelCounts((prev) => ({
                    ...prev,
                    [level]: { count: response.data.count || 0, loading: false },
                }));
            } else {
                setLevelCounts(prev => ({ ...prev, [level]: { count: 0, loading: false } }));
            }
        } catch (error) {
            console.error(`Error fetching count for ${level}:`, error);
            setLevelCounts(prev => ({ ...prev, [level]: { count: 0, loading: false } }));
        }
    };

    useEffect(() => {
        if (isAllowedProcess && activeWorkflow) {
            lastThreeLevels.forEach(lvl => {
                const isLevelExists = userWorkflows?.some((uw: any) => uw.userLevel === lvl);
                if (isLevelExists) {
                    fetchLevelData(lvl);
                }
            });
        }
    }, [processName, activeWorkflow, startDate, endDate, userWorkflows]);

    if (!isAllowedProcess) return null;

    const cardStyles = [
        {
            bg: 'linear-gradient(135deg, #3182ce 0%, #2b6cb0 100%)',
            activeBg: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
            shadow: '0 10px 15px -3px rgba(49, 130, 206, 0.3)',
            glow: '0 0 10px rgba(66, 153, 225, 0.6)'
        },
        {
            bg: 'linear-gradient(135deg, #38b2ac 0%, #2c7a7b 100%)',
            activeBg: 'linear-gradient(135deg, #4fd1c5 0%, #38b2ac 100%)',
            shadow: '0 10px 15px -3px rgba(56, 178, 172, 0.3)',
            glow: '0 0 20px rgba(79, 209, 197, 0.6)'
        },
        {
            bg: 'linear-gradient(135deg, #805ad5 0%, #6b46c1 100%)',
            activeBg: 'linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)',
            shadow: '0 10px 15px -3px rgba(128, 90, 213, 0.3)',
            glow: '0 0 20px rgba(159, 122, 234, 0.6)'
        }
    ];

    return (
        <Grid
            templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
            gap={4}
            mb={6}
            w="full"
        >
            {lastThreeLevels.map((level, index) => {
                const isSelected = selectedLevel === level;
                const style = cardStyles[index % 3];
                const data = levelCounts[level] || { count: 0, loading: false };

                return (
                    <Box
                        key={level}
                        bg={isSelected ? style.activeBg : style.bg}
                        p={4}
                        rounded="xl"
                        shadow={isSelected ? style.glow : style.shadow}
                        position="relative"
                        overflow="hidden"
                        color="white"
                        cursor="pointer"
                        transition="all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                        onClick={() => setSelectedLevel(level)}
                        transform={isSelected ? "translateY(-8px) scale(1.02)" : "none"}
                        _hover={{
                            transform: "translateY(-4px)",
                            shadow: isSelected ? style.glow : 'xl'
                        }}
                    >
                        {/* Decorative Background Icon */}
                        <Icon
                            as={FaLayerGroup}
                            position="absolute"
                            right="-10px"
                            bottom="-10px"
                            fontSize="4xl"
                            opacity={0.15}
                            transform="rotate(-15deg)"
                        />

                        {/* Selection Indicator */}
                        {isSelected && (
                            <Circle
                                size="20px"
                                bg="white"
                                color={style.bg.includes('#3182ce') ? 'blue.500' : style.bg.includes('#38b2ac') ? 'teal.500' : 'purple.500'}
                                position="absolute"
                                top="4"
                                right="4"
                                shadow="md"
                                zIndex={2}
                            >
                                <Icon as={FaCheckCircle} />
                            </Circle>
                        )}

                        <Flex direction="column" position="relative" zIndex={1}>
                            <Text
                                fontSize="xs"
                                fontWeight="black"
                                textTransform="uppercase"
                                letterSpacing="1px"
                                opacity={0.8}
                                mb={1}
                            >
                                {isSelected ? "Active View" : "Pending Stage"} At your Approval
                            </Text>
                            <Text fontSize="lg" fontWeight="900" mb={1} lineHeight="1.2">
                                {levelLabels[processName]?.[level] || level.replace('level-', 'Level ')} - Pending
                            </Text>

                            <Box mt={3}>
                                <Flex align="baseline" gap={2}>
                                    <Text fontSize="2xl" fontWeight="1000" lineHeight={1}>
                                        {data.count}
                                    </Text>
                                    <Text fontSize="sm" fontWeight="bold" opacity={0.9}>
                                        Document
                                    </Text>
                                </Flex>
                                <Box
                                    h="3px"
                                    w="30px"
                                    bg="white"
                                    mt={3}
                                    rounded="full"
                                    opacity={isSelected ? 0.3 : 0.3}
                                    transition="all 0.3s"
                                />
                            </Box>
                        </Flex>
                    </Box>
                );
            })}
        </Grid>
    );
};

export default ProcessLevelCards;
